export type User = {
  id: string
  email: string | null
  displayName: string
  avatarUrl: string | null
  timezone: string
  /** Технический администратор сервера — видит состояние, но не содержимое */
  isAdmin: boolean
}

export type FamilyMember = {
  id: string
  displayName: string
  avatarUrl: string | null
  birthday: string | null
  role: 'OWNER' | 'ADULT' | 'TEEN' | 'CHILD'
  online: boolean
  lastSeenAt: string | null
}

export type Circle = {
  id: string
  name: string
  color: string | null
  isDefault: boolean
  memberIds: string[]
}

export type Family = {
  id: string
  name: string
  avatarUrl: string | null
  currency: string
  members: FamilyMember[]
  circles: Circle[]
}

export type CardAction = {
  id: string
  label: string
  style?: 'primary' | 'quiet'
}

/** Живая карточка объекта из любого раздела. */
export type Card = {
  entityType: string
  entityId: string
  section: 'shopping' | 'planner' | 'movies' | 'wishlist' | 'health' | 'wallet' | 'recipes'
  title: string
  subtitle?: string
  badge?: string
  imageUrl?: string
  progress?: { done: number; total: number }
  done?: boolean
  actions: CardAction[]
  href: string
}

/** Объект удалён или закрыт от читателя — показываем подпись из истории. */
export type UnavailableCard = {
  entityType: string
  entityId: string
  unavailable: true
  title: string
}

export type AnyCard = Card | UnavailableCard

export const isUnavailable = (card: AnyCard): card is UnavailableCard =>
  'unavailable' in card && card.unavailable

/** Файл, приложенный к сообщению. */
/**
 * Чем вложение является для человека.
 *
 * Тип содержимого этого не говорит: «video/mp4» — это и кружок,
 * записанный в переписке, и фильм из галереи. Решает отправитель
 * в момент отправки.
 */
export type FileKind = 'VOICE' | 'ROUND' | 'PHOTO' | 'VIDEO' | 'AUDIO' | 'FILE'

export type MessageFile = {
  id: string
  kind: FileKind
  fileName: string
  mimeType: string
  sizeBytes: number
  width: number | null
  height: number | null
  /** Длительность голосового или кружка */
  durationSec: number | null
  /** Огибающая громкости — по ней рисуется дорожка */
  waveform: number[] | null
  url: string
  /** Есть только у картинок; у остальных клиент рисует значок */
  thumbUrl: string | null
}

/** Опрос внутри сообщения. */
export type PollOption = {
  id: string
  text: string
  count: number
  percent: number
  voters: { id: string; displayName: string; avatarUrl: string | null }[]
}

export type Poll = {
  id: string
  question: string
  multiple: boolean
  anonymous: boolean
  closed: boolean
  authorName: string | null
  totalVoters: number
  myVote: string[]
  options: PollOption[]
}

export type Reaction = {
  emoji: string
  count: number
  mine: boolean
}

export type Message = {
  id: string
  chatId: string
  body: string | null
  isSystem: boolean
  deleted: boolean
  author: { id: string; displayName: string; avatarUrl: string | null } | null
  mine: boolean
  /** Имя того, от кого переслано */
  forwardedFrom: string | null
  replyTo: {
    id: string
    body: string | null
    authorName: string | null
    /** Оригинал удалён: текста в цитате нет и переходить некуда */
    deleted?: boolean
  } | null
  cards: AnyCard[]
  files: MessageFile[]
  poll: Poll | null
  reactions: Reaction[]
  createdAt: string
  editedAt: string | null
  /** Отложенное: до срока его видит только тот, кто написал */
  scheduledAt?: string | null
  /** Закреплено в шапке. Закреплённых в переписке может быть несколько */
  pinned?: boolean
  /** Ещё не ушло: набрано без связи и ждёт своей очереди */
  pending?: boolean
  /** Последняя попытка отправки не удалась */
  failed?: boolean
  /** Сколько процентов вложения уже ушло: 0…100, пока идёт отправка */
  uploadPercent?: number
}

export type ChatSummary = {
  id: string
  kind: 'DIRECT' | 'GROUP' | 'ENTITY' | 'SAVED'
  title: string
  avatarUrl: string | null
  muted: boolean
  pinned: boolean
  archived: boolean
  unread: number
  /** В этой переписке позвали по имени и это ещё не прочитано */
  mentioned?: boolean
  membersCount: number
  online: boolean
  lastMessage: {
    id: string
    body: string | null
    isSystem: boolean
    authorName: string | null
    authorId: string | null
    createdAt: string
  } | null
}

export type PinnedMessage = {
  id: string
  body: string | null
  authorName: string | null
}

export type ChatDetail = {
  id: string
  kind: 'DIRECT' | 'GROUP' | 'ENTITY' | 'SAVED'
  title: string
  avatarUrl: string | null
  description: string | null
  pinnedMessage: PinnedMessage | null
  members: {
    id: string
    displayName: string
    avatarUrl: string | null
    isAdmin: boolean
    online: boolean
    lastSeenAt: string | null
    /** До какого момента человек дочитал — по этому рисуются галочки */
    lastReadAt: string | null
  }[]
}

/** Найденное сообщение в поиске. */
export type SearchResult = {
  id: string
  chatId: string
  chatTitle: string | null
  chatKind: 'DIRECT' | 'GROUP' | 'ENTITY' | 'SAVED'
  body: string | null
  author: { id: string; displayName: string; avatarUrl: string | null } | null
  createdAt: string
}

/** События, приходящие по постоянному соединению. */
export type ServerEvent =
  | { type: 'message:new'; chatId: string; message: Message }
  | { type: 'message:edited'; chatId: string; message: Message }
  | { type: 'message:deleted'; chatId: string; messageId: string }
  | { type: 'chat:typing'; chatId: string; userId: string; displayName: string }
  | { type: 'chat:read'; chatId: string; userId: string; readAt: string }
  | { type: 'chat:pinned'; chatId: string; messageId: string | null }
  | { type: 'card:changed'; entityType: string; entityId: string }
  | { type: 'call:incoming'; call: Call; callerName: string; chatTitle: string }
  | { type: 'call:updated'; call: Call }
  | { type: 'call:ended'; callId: string; chatId: string; missed: boolean }
  | { type: 'call:signal'; callId: string; fromUserId: string; payload: unknown }
  | { type: 'pong' }
  /** Человек появился в сети или ушёл из неё */
  | { type: 'user:presence'; userId: string; online: boolean; lastSeenAt: string }

export type Call = {
  id: string
  chatId: string
  kind: 'AUDIO' | 'VIDEO'
  status: 'RINGING' | 'ACTIVE' | 'ENDED' | 'MISSED'
  startedAt: string
  startedById: string | null
  participants: { userId: string; joined: boolean; left: boolean }[]
}
